import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { Megaphone, CaretRight } from "@phosphor-icons/react"
import { Button } from "../../../components/ui/button"
import { cn } from "../../../lib/utils"
import { useChangelogEntries, useChangelogUnreadCount, useMarkChangelogViewed } from "../hooks/useChangelog"
import { ChangelogEntryCard } from "./ChangelogEntryCard"
import { useAuthStore } from "../../../stores/auth-store"

export function ChangelogButton() {
  const { user } = useAuthStore()
  const canView = user?.privileges?.includes("VIEW.CAN_VIEW_CHANGELOG")

  if (!canView) return null

  return <ChangelogButtonInner />
}

function ChangelogButtonInner() {
  const [isOpen, setIsOpen] = useState(false)
  const [badgeAnimating, setBadgeAnimating] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const prevCountRef = useRef<number>(0)

  const { data: count = 0 } = useChangelogUnreadCount()
  const { data } = useChangelogEntries({ limit: 5 })
  const markViewed = useMarkChangelogViewed()

  // Animate badge when count increases
  useEffect(() => {
    if (count > prevCountRef.current && prevCountRef.current > 0) {
      setBadgeAnimating(true)
      setTimeout(() => setBadgeAnimating(false), 300)
    }
    prevCountRef.current = count
  }, [count])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const handleToggle = () => {
    const opening = !isOpen
    setIsOpen(opening)
    if (opening && count > 0) {
      markViewed.mutate()
    }
  }

  const entries = data?.entries ?? []

  return (
    <div ref={menuRef} className="relative">
      <Button
        variant="ghost"
        size="icon-sm"
        className="relative group"
        onClick={handleToggle}
        title="What's New"
      >
        <Megaphone
          className={cn(
            "h-4 w-4 transition-colors",
            isOpen ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"
          )}
        />
        {count > 0 && (
          <span
            className={cn(
              "absolute -top-0.5 -right-0.5",
              "flex h-[18px] min-w-[18px] items-center justify-center",
              "rounded-full px-1",
              "text-[10px] font-bold text-white",
              "bg-gradient-to-br from-primary to-primary-600",
              "shadow-sm shadow-primary/25",
              "transition-transform duration-200",
              "group-hover:scale-110",
              badgeAnimating && "animate-notification-pop"
            )}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </Button>

      {isOpen && (
        <div
          className={cn(
            "absolute right-0 top-full mt-2 rounded-xl",
            "w-[calc(100vw-2rem)] sm:w-96",
            "bg-background border border-border shadow-xl",
            "overflow-hidden z-50",
            "animate-flyout-enter"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">What's New</h3>
          </div>

          {/* Entry list */}
          <div className="max-h-80 overflow-y-auto divide-y divide-border">
            {entries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <Megaphone className="h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No updates yet</p>
              </div>
            ) : (
              entries.map((entry) => (
                <ChangelogEntryCard key={entry._id} entry={entry} compact />
              ))
            )}
          </div>

          {/* Footer */}
          <Link
            to="/changelog"
            onClick={() => setIsOpen(false)}
            className={cn(
              "flex items-center justify-center gap-1 w-full px-4 py-3",
              "text-sm text-primary font-medium",
              "border-t border-border",
              "hover:bg-muted transition-colors"
            )}
          >
            View all updates
            <CaretRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  )
}
